import { useFeatureFlag } from '@ctw/shared/context/feature-flag-provider';
import { useCtwQuery } from '@ctw/shared/hooks/use-ctw-query';
import { getBffApiBaseUrl } from '@ctw/shared/utils/environments';
import { QUERY_KEY_HOTLOAD_STATUS } from '@ctw/shared/utils/query-keys';
import type { Query } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

// How often to re-check the status endpoint while a load is still in progress.
export const HOTLOAD_POLL_INTERVAL_MS = 300;
// How long to wait before giving up on a load and letting the gated view fall through to its
// own loading / empty states. We never want to trap a user on a spinner indefinitely.
export const HOTLOAD_TIMEOUT_MS = 120_000;

// The five states the BFF's hotload status endpoint reports for a UPID's presence in hotstorage.
// `partially_hotloaded` means at least one resource type never loaded at all. `hotloaded_with_omissions`
// means every resource type loaded, but at least one type skipped individual resources along the
// way — it is rollup-only, a per-type entry in `resourceTypes` never carries this value.
export type HotloadState =
  | 'not_hotloaded'
  | 'hotloading'
  | 'hotloaded'
  | 'partially_hotloaded'
  | 'hotloaded_with_omissions';

// States that will never change again this session: nothing left to poll for. `not_hotloaded` and
// `hotloading` are excluded, since those are the only states a load can still progress out of.
const TERMINAL_HOTLOAD_STATES = new Set<HotloadState>([
  'hotloaded',
  'partially_hotloaded',
  'hotloaded_with_omissions',
]);

const isTerminalHotloadState = (state?: HotloadState): boolean =>
  state !== undefined && TERMINAL_HOTLOAD_STATES.has(state);

// The BFF wraps the hotload probe in a JSON:API envelope.
interface HotloadStatusAttributes {
  upid: string;
  state: HotloadState;
  // The resource types already in hotstorage, derived by the BFF from FQS's per-type states. This
  // accrues *while* the overall load is still `hotloading` (a type appears as soon as its own load
  // finishes), which is what lets `<HotloadGate>` unblock per-resource ahead of the full rollup.
  loadedResourceTypes?: Array<string>;
  // The resource types FQS reports as loaded but with at least one resource skipped along the way.
  // A type here is still in `loadedResourceTypes` — its rows are readable, just incomplete.
  incompleteResourceTypes?: Array<string>;
  // How many resources of each partially loaded type were skipped because they failed to load on
  // their own. A type with no entry here has no stored count — either it isn't partially loaded,
  // or it was marked partially loaded before FQS started persisting this count and hasn't reloaded
  // since. That's distinct from an entry of 0, which FQS never actually emits.
  skippedResourceCountByType?: Record<string, number>;
}

interface HotloadStatusEnvelope {
  data: {
    id: string;
    type: string;
    attributes: HotloadStatusAttributes;
  };
}

export interface UseHotloadStatusResult {
  state?: HotloadState;
  loadedResourceTypes: Array<string>;
  incompleteResourceTypes: Array<string>;
  skippedResourceCountByType: Record<string, number>;
  isSettled: boolean;
  hasTimedOut: boolean;
  isError: boolean;
}

export interface UseHotloadStatusOptions {
  /**
   * Whether to keep polling `HOTLOAD_POLL_INTERVAL_MS` apart until the patient's hotload reaches
   * a terminal state (or the timeout elapses). Defaults to `true`. Pass `false` for a consumer
   * that only needs a single read — a UPID that hasn't yet settled never satisfies `isSettled`,
   * so a poller would otherwise run for the full `HOTLOAD_TIMEOUT_MS` on every mount. A `false`
   * reader also never arms the timeout: there is no ongoing poll for it to end, so `hasTimedOut`
   * stays `false`.
   */
  poll?: boolean;
}

/**
 * Reads the BFF `GET /v1/patient/hotload/status/{upid}` endpoint, polling `HOTLOAD_POLL_INTERVAL_MS`
 * apart until the patient's hotload reaches a terminal state (`hotloaded`, `partially_hotloaded`, or
 * `hotloaded_with_omissions` — see `isSettled`), the request hard-errors, or `HOTLOAD_TIMEOUT_MS`
 * elapses, unless `options.poll` is `false`, in which case it fetches once. Keyed on the UPID so
 * every polling consumer shares a single poll via React Query deduplication (a `poll: false` read
 * shares that same cache entry, but does not itself keep it refreshing).
 *
 * @param upid The UPID of the patient
 */
export const useHotloadStatus = (
  upid?: string,
  options: UseHotloadStatusOptions = {},
): UseHotloadStatusResult => {
  const { poll = true } = options;
  const hasHotloadBlocking = useFeatureFlag('ctw-hotload-blocking');
  const [hasTimedOut, setHasTimedOut] = useState(false);

  const enabled = hasHotloadBlocking && !!upid && !hasTimedOut;

  const query = useCtwQuery<HotloadStatusAttributes>({
    queryId: QUERY_KEY_HOTLOAD_STATUS,
    queryKey: [upid],
    queryFn: async ({ requestContext, ctwFetch }) => {
      const { data, ok } = await ctwFetch<HotloadStatusEnvelope>(
        `${getBffApiBaseUrl(requestContext.env)}/v1/patient/hotload/status/${upid}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${requestContext.authToken}`,
          },
          mode: 'cors',
        },
      );

      // `ctwFetch` swallows network / non-2xx failures (already reported to telemetry) and
      // returns `ok: false`. Throw so React Query marks the query as errored and the gate falls
      // through rather than spinning forever.
      if (!ok) {
        throw new Error('Failed to fetch hotload status');
      }

      return data.data.attributes;
    },
    enabled,
    // Keep polling while the load is still progressing; stop the moment it reaches a terminal
    // state. The timeout and error paths stop polling by flipping `enabled` / erroring rather than
    // via this interval. `poll: false` disables the interval outright rather than letting it fire
    // once and stop on its own, since a poll-disabled reader has no business scheduling a repeat
    // fetch at all.
    refetchInterval: poll
      ? (polledQuery: Query<HotloadStatusAttributes>) =>
          isTerminalHotloadState(polledQuery.state.data?.state) ? false : HOTLOAD_POLL_INTERVAL_MS
      : false,
  });

  const isSettled = isTerminalHotloadState(query.data?.state);

  // Reset the timeout latch when the patient changes — otherwise a UPID that timed out would keep
  // the query permanently disabled for every subsequent patient (the hook instance persists across
  // patient navigations in the shared `[upid]` layout).
  // biome-ignore lint/correctness/useExhaustiveDependencies: `upid` is the intended trigger even though the effect body doesn't read it.
  useEffect(() => {
    setHasTimedOut(false);
  }, [upid]);

  // Arm a one-shot timeout while we're actively waiting on a load. Clearing on `isSettled` (and on
  // `enabled` flipping off once timed out) means a fast load never trips the fallthrough. `upid` is
  // a dependency so the timer restarts with a fresh deadline per patient. Skipped entirely when
  // `poll` is `false`: there is no ongoing poll to give up on, so arming it would only start a
  // needless 120-second timer on every mount.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `upid` intentionally restarts the timer on patient change even though the effect body doesn't read it.
  useEffect(() => {
    if (!(poll && enabled) || isSettled) {
      return;
    }

    const timer = setTimeout(() => setHasTimedOut(true), HOTLOAD_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [poll, enabled, isSettled, upid]);

  return {
    state: query.data?.state,
    loadedResourceTypes: query.data?.loadedResourceTypes ?? [],
    incompleteResourceTypes: query.data?.incompleteResourceTypes ?? [],
    skippedResourceCountByType: query.data?.skippedResourceCountByType ?? {},
    isSettled,
    hasTimedOut,
    isError: query.isError,
  };
};
